import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  MutationCache, QueryCache, QueryClient, QueryClientContext, hashKey, useQuery, useQueryClient,
} from '@tanstack/react-query';
import type { Id, Version } from '@contracts/common';
import type { MeDTO } from '@contracts/identity';
import type { ObjectRef, Permission, PermissionMap } from '@contracts/rbac';
import { allows, hasPermission } from '@domain/rbac';
import { forgetPerson } from '../lib/person-storage';
import { api, ApiClientError } from './client';

/**
 * The two pieces of state every screen shares: who is signed in, and what happens to a form when the server
 * refuses it. Both exist so that no screen has to invent its own answer — particularly for the second one,
 * where the wrong answer (clearing the form, or resending it) is the one that loses somebody's work.
 */

export const PRINCIPAL_KEY = ['principal'] as const;
const PRINCIPAL_HASH = hashKey(PRINCIPAL_KEY);

/** `null` means "not signed in", which is an answer, not a failure; anything else is a real error. */
export function usePrincipal() {
  return useQuery<MeDTO | null>({
    queryKey: PRINCIPAL_KEY,
    queryFn: async () => {
      try {
        return await api<MeDTO>('/api/me');
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
}

/** A refusal that says the principal's permissions changed under it: the web re-reads `/api/me` (RBAC spec §5.3). */
export const isForbidden = (error: unknown): boolean => error instanceof ApiClientError && error.status === 403;

/** The `meta` of a query a signed-out page makes (an invitation or reset link): its 401 is its own answer. */
export const PUBLIC_QUERY_META = { publicPage: true } as const;

/** What else holds one person's data beside the query cache, per client (the board's session in useBoard.ts). */
const sessionStores = new WeakMap<QueryClient, Set<() => void>>();

/** Registers `reset`, run whenever a session ends on `queryClient` (see `endSession`). */
export function onSessionEnd(queryClient: QueryClient, reset: () => void): void {
  let resets = sessionStores.get(queryClient);
  if (resets === undefined) {
    resets = new Set();
    sessionStores.set(queryClient, resets);
  }
  resets.add(reset);
}

/**
 * The session of `userId` ended in this tab — "Đăng xuất", a 401, or someone else signing in (authz audit D-F1): every
 * cached answer goes (all but the principal when `keepPrincipal`, which then already holds who comes next), every
 * store registered with `onSessionEnd` is reset, and the person's card ids leave this browser's storage.
 */
export function endSession(
  queryClient: QueryClient,
  userId: Id | undefined = queryClient.getQueryData<MeDTO | null>(PRINCIPAL_KEY)?.userId,
  keepPrincipal = false,
): void {
  queryClient.removeQueries({ predicate: (query) => !keepPrincipal || query.queryKey[0] !== PRINCIPAL_KEY[0] });
  for (const reset of sessionStores.get(queryClient) ?? []) reset();
  forgetPerson(userId);
}

/**
 * A 401 says the session is gone (expired, revoked, the account disabled — authz audit D-F3): the principal becomes
 * `null`, which sends the shell to the login page, and the session ends with it. Only for a signed-in tab: the
 * login, invitation and reset pages have no principal, and their own refusals are answers they show.
 */
function endOnUnauthenticated(queryClient: QueryClient | undefined, error: unknown): void {
  if (queryClient === undefined || !(error instanceof ApiClientError) || error.status !== 401) return;
  const principal = queryClient.getQueryData<MeDTO | null>(PRINCIPAL_KEY);
  if (principal == null) return;
  endSession(queryClient, principal.userId, true);
  queryClient.setQueryData<MeDTO | null>(PRINCIPAL_KEY, null);
}

/**
 * For a write made with a plain `api()` call, outside `usePreservedMutation`: hand it the error in the `catch`
 * and a 403 re-reads the principal, as the query cache does for reads (a 401 ends the session). The client is
 * optional for the same reason as in `usePreservedMutation`.
 */
export function useRefreshOnForbidden(): (error: unknown) => void {
  const queryClient = useContext(QueryClientContext);
  return useCallback((error: unknown) => {
    if (isForbidden(error)) void queryClient?.invalidateQueries({ queryKey: PRINCIPAL_KEY });
    endOnUnauthenticated(queryClient, error);
  }, [queryClient]);
}

/**
 * The app's one query client. Nothing is retried — not a read, and least of all a write — because a repeat of a
 * request the server may already have applied is how one action becomes two. A 403 from any query re-reads the
 * principal, so a revoked permission takes its rows and buttons with it without a reload. The web's writes do
 * not go through `useMutation` (they use `usePreservedMutation`, or `api()` with `useRefreshOnForbidden`); the
 * mutation cache's handler is kept so that a `useMutation` added later is covered without anyone remembering to.
 */
export function createQueryClient(): QueryClient {
  const refreshPrincipal = (error: unknown) => {
    if (isForbidden(error)) void queryClient.invalidateQueries({ queryKey: PRINCIPAL_KEY });
    endOnUnauthenticated(queryClient, error);
  };
  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => { if (query.meta?.['publicPage'] !== true) refreshPrincipal(error); },
    }),
    mutationCache: new MutationCache({ onError: refreshPrincipal }),
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
  });
  // Whoever held the tab, once the principal stops being them (a re-read answered `null` or someone else), nothing
  // they left in this client may reach the next person (authz audit D-F1).
  let signedIn: Id | undefined;
  queryClient.getQueryCache().subscribe((event) => {
    if (event.query.queryHash !== PRINCIPAL_HASH) return;
    if (event.type !== 'updated' && event.type !== 'removed') return;
    const now = event.type === 'removed' ? undefined : (event.query.state.data as MeDTO | null | undefined)?.userId;
    const was = signedIn;
    signedIn = now;
    if (was !== undefined && now !== was) endSession(queryClient, was, true);
  });
  return queryClient;
}

/** The three questions a screen asks of the permission map (RBAC spec §5.3). */
export type Can = {
  /** Held at any scope: an entry point (a sidebar row, a page, a card) is shown. */
  any: (perm: Permission) => boolean;
  /** Held company-wide: a create button, or anything not tied to one object. */
  all: (perm: Permission) => boolean;
  /** Held on this object: a per-object edit or delete button. */
  on: (perm: Permission, ref: ObjectRef) => boolean;
};

const NO_PERMISSIONS: PermissionMap = {};

/** What the signed-in principal may do, read from the same map the server enforces. */
export function useCan(): Can {
  const map = usePrincipal().data?.permissions ?? NO_PERMISSIONS;
  return useMemo(() => ({
    any: (perm) => hasPermission(map, perm),
    all: (perm) => allows(map, perm),
    on: (perm, ref) => allows(map, perm, ref),
  }), [map]);
}

export function useSignOut(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST', body: {} });
    } catch {
      // A refused or unreachable logout is not something to report: the session cookie may already be
      // gone, and there is no useful answer to give somebody who has asked to leave. It never becomes a
      // rejection, so the caller's "now go to the login screen" always runs.
    } finally {
      // Whatever the server said, this browser must stop showing the previous person's screens.
      endSession(queryClient);
      queryClient.clear();
    }
  }, [queryClient]);
}

/** The answer to one submission. It is a value, not an exception: a refusal is an ordinary outcome here. */
export type MutationOutcome<TResult> =
  | { ok: true; result: TResult }
  | { ok: false; error: ApiClientError };

export type PreservedMutation<TInput, TResult> = {
  /** Never rejects. Resolves with the outcome, or with `null` when a submission was already in flight. */
  submit: (input: TInput) => Promise<MutationOutcome<TResult> | null>;
  busy: boolean;
  error: ApiClientError | null;
  /** The server's per-field messages, ready for `ErrorSummary`. */
  fields: Record<string, string>;
  /** Set when the refusal was a version conflict, so a conflict panel can compare. */
  currentVersion: Version | undefined;
  reset: () => void;
  /**
   * Starts a new request id. For a form whose next submission is a different intent from the failed one (the draft put
   * back, the newest read loaded, or the server saying the id was used for another body): replaying the old id with a
   * new body would only be refused as `REQUEST_CONFLICT`.
   */
  rotate: () => void;
};

/**
 * One submission of one form. Three rules it exists to enforce:
 *
 *  - a refused submission is never repeated by the client, so a save that may or may not have landed stays
 *    the person's decision;
 *  - the caller's form state is never touched, so a refusal leaves the typing exactly where it was — except a 401:
 *    a dead session ends here (`endOnUnauthenticated`) and the shell unmounts the form on its way to sign-in, since
 *    another person's data must not stay on screen (authz audit D-F3);
 *  - a re-submission after a failure carries the *same* request id, so if the first attempt did reach the
 *    server the replay is recognised instead of duplicated. The id only rotates once something succeeded.
 */
export function usePreservedMutation<TInput, TResult>(
  submit: (input: TInput, requestId: Id) => Promise<TResult>,
): PreservedMutation<TInput, TResult> {
  // Optional: a form rendered on its own (a unit test, a public page) has no client and nothing to refresh.
  const queryClient = useContext(QueryClientContext);
  const requestId = useRef<Id>(crypto.randomUUID());
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiClientError | null>(null);

  const run = useCallback(async (input: TInput): Promise<MutationOutcome<TResult> | null> => {
    if (inFlight.current) return null;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await submit(input, requestId.current);
      requestId.current = crypto.randomUUID();
      return { ok: true, result };
    } catch (cause) {
      const failure = cause instanceof ApiClientError
        ? cause
        : new ApiClientError(0, 'UNKNOWN', 'Không thể lưu. Hãy thử lại.');
      // These submissions do not go through the mutation cache, so they re-read the principal themselves.
      if (isForbidden(failure)) void queryClient?.invalidateQueries({ queryKey: PRINCIPAL_KEY });
      endOnUnauthenticated(queryClient, failure);
      setError(failure);
      return { ok: false, error: failure };
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [submit, queryClient]);

  return {
    submit: run,
    busy,
    error,
    fields: error?.fields ?? {},
    currentVersion: error?.currentVersion,
    reset: useCallback(() => setError(null), []),
    rotate: useCallback(() => { requestId.current = crypto.randomUUID(); }, []),
  };
}
